"use client";

import GiftIcon2 from "@/assets/icons/invite-gift-2.svg";
import GiftIcon from "@/assets/icons/invite-gift.svg";
import LinkIcon from "@/assets/icons/invite-link.svg";
import ModalCloseIcon from "@/assets/icons/modal-close.svg";
import { logger } from "@/common/logger";
import { useReferralCode } from "@/hooks/useReferralCode";
import { useShareLink } from "@/hooks/useShareLink";
import { Modal } from "antd";
import { useEffect, useMemo, useState } from "react";
import { VidMuseEvent } from "../../common/events";
import Button from "../Button";
import { Loading } from "../Loading";

export interface InviteGuideModalProps {
  open: boolean;
  inviteLink?: string;
  onClose: () => void;
  onCopy?: (link: string) => void;
}

const buildInviteLink = (code: string) => {
  if (!code || typeof window === "undefined") {
    return "";
  }

  return `${window.location.origin}?referral=${encodeURIComponent(code)}`;
};

export function InviteGuideModal({
  open,
  inviteLink: inviteLinkProp,
  onClose,
  onCopy,
}: InviteGuideModalProps) {
  const { referralCode, loading } = useReferralCode(open && !inviteLinkProp);
  const [isCopied, setIsCopied] = useState(false);
  const resolvedInviteLink = useMemo(() => {
    const code = inviteLinkProp ?? referralCode;
    return buildInviteLink(code);
  }, [inviteLinkProp, referralCode]);
  const { handleCopyLink } = useShareLink({ shareLink: resolvedInviteLink });

  const canCopy = !!resolvedInviteLink;

  const handleCopy = () => {
    if (!canCopy) {
      return;
    }

    const success = handleCopyLink();
    if (success) {
      onCopy?.(resolvedInviteLink);
    }

    if (success) {
      setIsCopied(true);
      logger.event(VidMuseEvent.InviteGuideModalCopyLinkBtnClick);
    }
  };

  useEffect(() => {
    if (!isCopied) return;
    const timer = setTimeout(() => setIsCopied(false), 3000);
    return () => clearTimeout(timer);
  }, [isCopied]);

  // 弹窗显示时发送曝光事件
  useEffect(() => {
    if (open) {
      logger.event(VidMuseEvent.InviteGuideModalDisplayed);
    }
  }, [open]);

  return (
    <Modal
      open={open}
      centered
      closable={false}
      maskClosable
      footer={null}
      width={456}
      onCancel={onClose}
      styles={{
        mask: {
          background: "rgba(0, 0, 0, 0.85)",
        },
      }}
      classNames={{
        content:
          "overflow-hidden w-[456px] rounded-[20px]! border border-white bg-[rgba(255,255,255,0.85)]! p-0! backdrop-blur-2xl",
        body: "p-0",
      }}
    >
      <div className="relative py-9 px-7 text-black">
        <button
          type="button"
          className="absolute right-5 top-4 rounded-full p-1 text-black/20 transition hover:text-black/70 cursor-pointer"
          onClick={onClose}
          aria-label="Close invite guide"
          data-testid="invite-guide-close"
        >
          <ModalCloseIcon className="h-5 w-5" />
        </button>

        <h2 className="text-center text-xl font-medium">
          Invite friends and earn free credits
        </h2>

        <p className="mt-6 text-base">
          Rule: When other users access VidMuse.ai via your referral link and
          log in
        </p>

        <div className="mt-5 space-y-4 text-sm">
          <div className="flex gap-2">
            <GiftIcon className="shrink-0" />
            <div>
              <p>500 free credits will be awarded to you</p>
              <p className="text-black/40 mt-1">
                When other users generate a complete video for the first time
                and export it (Max 10 rewards)
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <GiftIcon2 className="shrink-0" />
            <div>
              <p>2,000 credits will be awarded to you</p>
              <p className="text-black/40 mt-1">
                If they subscribe to any paid plan for the first time.
              </p>
            </div>
          </div>
        </div>

        <p className="mt-5 text-sm text-black/60">My sharing link</p>

        <div className="mt-2 flex items-center rounded-full text-black bg-black/[0.04] pl-5">
          <LinkIcon className="h-4 w-4" />
          <p className="min-w-0 flex-1 truncate text-sm ml-2">
            {loading ? (
              <>
                <span className="text-black/40">Loading...</span> <Loading />
              </>
            ) : (
              resolvedInviteLink
            )}
          </p>
          <Button
            type="primary"
            className="h-10 rounded-full px-5 text-sm font-medium"
            onClick={handleCopy}
            disabled={!canCopy || isCopied}
            data-testid="invite-guide-copy"
          >
            {isCopied ? "Link copied" : "Copy Link"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default InviteGuideModal;
